import { useCallback, useMemo } from 'react'
import _ from 'lodash'

import { useApiQuery, mergeApiRequests, Queries } from 'admin/lib/Api'

export function useRecordLoader(accountId, projectId, moduleName, recordId) {
  const recordQuery = Queries.useRecord(
    accountId,
    projectId,
    moduleName,
    recordId
  )

  function setInfractionIsPendingProperty(wrappedResponse, isPending) {
    const wrappedResponseData = wrappedResponse[0]
    return [
      Array.isArray(wrappedResponseData)
        ? wrappedResponseData.map(i => ({ ...i, isPending: isPending }))
        : wrappedResponseData,
      ...wrappedResponse.slice(1)
    ]
  }

  const infractions = useApiQuery(
    useCallback(
      api =>
        mergeApiRequests({
          openInfractions: api
            .getInfractions(accountId, projectId, moduleName, recordId)
            .then(r => setInfractionIsPendingProperty(r, false)),
          pendingInfractions: api
            .getPendingInfractions(accountId, projectId, moduleName, recordId)
            .then(r => setInfractionIsPendingProperty(r, true))
        }),
      [accountId, moduleName, projectId, recordId]
    )
  )

  const infractionsData = useMemo(() => {
    const { openInfractions, pendingInfractions } = infractions.data ?? {}
    const total =
      (openInfractions?.length ?? 0) + (pendingInfractions?.length ?? 0)
    if (!infractions.loaded || total === 0) {
      return {}
    }

    return _(openInfractions)
      .union(pendingInfractions)
      .filter(infraction => Boolean(infraction.fieldName))
      .map(infraction => {
        infraction.fieldPathWithoutIndex = infraction.fieldName.replace(
          /\[\d+\]\./,
          '[_].'
        )
        infraction.fieldPathWithIndex = infraction.fieldName
        return infraction
      })
      .groupBy(infraction => infraction.fieldPathWithoutIndex)
      .value()
  }, [infractions.data, infractions.loaded])

  const recordContext = useMemo(() => {
    return {
      loaded: recordQuery.isFetched && infractions.loaded,
      status: `${recordQuery.status}|${infractions.status}`,
      record: recordQuery.data,
      infractions: infractionsData,
      error: recordQuery.error ?? infractions.error
    }
  }, [
    infractions.error,
    infractions.loaded,
    infractions.status,
    infractionsData,
    recordQuery.data,
    recordQuery.error,
    recordQuery.isFetched,
    recordQuery.status
  ])

  return recordContext
}
